// admin/src/screens/Users.jsx — list/search users (email, plan, balance, activity). Server
// paged via ?limit&offset (no total exists — Next is live while a full page comes back);
// columns sort client-side within the loaded page (an honest internal-tool sort, not a lie
// about the whole table). A negative balance is flagged red — the §2.4 alert surface at a
// glance; click through to move credits.

import React, { useEffect, useState } from 'react';
import { navigate } from '@common/router.js';
import { useAdmin } from '../App.jsx';
import { Banner, RolePill, fmtInt, fmtDateTime } from '../lib/ui.jsx';

const COLS = [
  { key: 'email', label: 'Email' },
  { key: 'display_name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'plan_id', label: 'Plan' },
  { key: 'balance', label: 'Balance', num: true },
  { key: 'jobs_30d', label: 'Jobs (30d)', num: true },
  { key: 'last_login_at', label: 'Last login', num: true },
];

export default function Users() {
  const { api } = useAdmin();
  const [q, setQ] = useState('');
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [sort, setSort] = useState({ col: null, dir: 1 });

  async function load(query, off) {
    setUsers(null);
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', String(limit));
    params.set('offset', String(off));
    const r = await api.get('admin/users?' + params.toString());
    if (r.kind === 'ok') { setUsers(r.body.users); setErr(null); }
    else { setUsers([]); setErr(r.error); }
  }
  useEffect(() => { load(q, offset); }, [offset, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  function search() { setOffset(0); load(q, 0); }
  function toggleSort(col) {
    setSort((s) => (s.col === col ? { col, dir: -s.dir } : { col, dir: 1 }));
  }

  const rows = (() => {
    if (!users) return null;
    if (!sort.col) return users;
    const c = sort.col;
    return [...users].sort((a, b) => {
      const av = a[c], bv = b[c];
      if (av == null && bv == null) return 0;
      if (av == null) return 1; // nulls last
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sort.dir;
      return String(av).localeCompare(String(bv)) * sort.dir;
    });
  })();

  return (
    <div>
      <div className="pagehead">
        <h1>Users &amp; credits</h1>
        <div className="pager">
          <label className="hint">rows
            <select value={limit} onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }} style={{ marginInlineStart: 6 }}>
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="btn tiny" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>‹ Prev</button>
          <span className="n">{offset + 1}–{offset + (users ? users.length : 0)}</span>
          <button className="btn tiny" disabled={!users || users.length < limit} onClick={() => setOffset(offset + limit)}>Next ›</button>
        </div>
      </div>
      <div className="row">
        <input placeholder="Search email or name…" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()} style={{ inlineSize: 320 }} />
        <button className="btn" onClick={search}>Search</button>
        {q && <button className="btn ghost tiny" onClick={() => { setQ(''); setOffset(0); load('', 0); }}>clear</button>}
      </div>
      {err && <Banner kind="err">{err}</Banner>}
      {!rows ? <p className="muted">Loading…</p> : rows.length === 0 ? <p className="muted">No users match.</p> : (
        <table>
          <thead>
            <tr>
              {COLS.map((c) => (
                <th key={c.key} className={'sortable' + (c.num ? ' num' : '')} onClick={() => toggleSort(c.key)}>
                  {c.label}{sort.col === c.key && <span className="ar"> {sort.dir > 0 ? '▲' : '▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="click" onClick={() => navigate(`/users/${u.user_id}`)}>
                <td>{u.email}</td>
                <td>{u.display_name || '—'}</td>
                <td><RolePill role={u.role} /></td>
                <td>{u.plan_id || '—'}</td>
                <td className={'mono num' + (u.balance < 0 ? ' neg' : '')}>{fmtInt(u.balance)}</td>
                <td className="mono num">{u.jobs_30d}</td>
                <td className="muted num">{fmtDateTime(u.last_login_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
